import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { searchPrograms, Program, ProgramSearchParams } from '../api/programs';
import { useLanguageStore } from '../store/languageStore';
import { useNeedStore } from '../store/needStore';

export const ProgramSearch: React.FC = () => {
  const { t } = useTranslation();
  const { selectedLanguage } = useLanguageStore();
  const { selectedNeed } = useNeedStore();
  const [zipCode, setZipCode] = useState('');
  const [radius, setRadius] = useState(25);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [programs, setPrograms] = useState<Program[]>([]);

  const handleSearch = async () => {
    if (!zipCode) {
      setError(t('Please enter a zip code'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const params: ProgramSearchParams = {
        zipCode,
        radius,
        languages: [selectedLanguage],
        need: selectedNeed
      };

      const results = await searchPrograms(params);
      setPrograms(results);
    } catch (err) {
      setError(t('Failed to search programs. Please try again.'));
      console.error('Error searching programs:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full max-w-4xl mx-auto p-4">
      <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
        <h2 className="text-2xl font-bold mb-4">{t('Find Programs Near You')}</h2>
        
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-1">
              {t('Zip Code')}
            </label>
            <input
              type="text"
              id="zipCode"
              value={zipCode}
              onChange={(e) => setZipCode(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('Enter your zip code')}
            />
          </div>
          
          <div className="w-full md:w-48">
            <label htmlFor="radius" className="block text-sm font-medium text-gray-700 mb-1">
              {t('Search Radius (miles)')}
            </label>
            <select
              id="radius"
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value={10}>10 {t('miles')}</option>
              <option value={25}>25 {t('miles')}</option>
              <option value={50}>50 {t('miles')}</option>
              <option value={100}>100 {t('miles')}</option>
            </select>
          </div>
        </div>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('Selected Need')}: <span className="font-semibold text-blue-600">{selectedNeed}</span>
          </label>
        </div>

        <button
          onClick={handleSearch}
          disabled={loading}
          className="w-full bg-blue-600 text-white py-3 px-6 rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? t('Searching...') : t('Search Programs')}
        </button>

        {error && (
          <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
            {error}
          </div>
        )}
      </div>

      {programs.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold mb-4">
            {t('Found')} {programs.length} {t('programs')} {t('near')} {zipCode}
          </h3>
          
          {programs.map((program) => (
            <div key={program.id} className="bg-white rounded-lg shadow-md p-6">
              <div className="flex justify-between items-start mb-4">
                <h4 className="text-lg font-semibold text-gray-900">{program.name}</h4>
                <span className="text-sm text-gray-600">{program.distance} {t('miles away')}</span>
              </div>
              
              <p className="text-gray-700 mb-4">{program.description}</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">{t('Services')}</h5>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {program.services.map((service, index) => (
                      <li key={index}>• {service}</li>
                    ))}
                  </ul>
                </div>
                
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">{t('Eligibility')}</h5>
                  <ul className="text-sm text-gray-600 space-y-1">
                    {program.eligibility.map((req, index) => (
                      <li key={index}>• {req}</li>
                    ))}
                  </ul>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h5 className="font-medium text-gray-900 mb-2">{t('Contact Information')}</h5>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="font-medium">{t('Phone')}:</span> {program.contact.phone}
                  </div>
                  <div>
                    <span className="font-medium">{t('Email')}:</span> {program.contact.email}
                  </div>
                  <div>
                    <span className="font-medium">{t('Website')}:</span> 
                    <a href={program.contact.website} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline ml-1">
                      {t('Visit')}
                    </a>
                  </div>
                </div>
                
                <div className="mt-2">
                  <span className="font-medium">{t('Address')}:</span> 
                  <span className="text-sm text-gray-600 ml-1">
                    {program.address.street}, {program.address.city}, {program.address.state} {program.address.zip}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}; 